import { computed, effectScope, ref, watch } from 'vue';
import {
  connectPrinter,
  printReceipt,
  scanPrinters,
  type FoundPrinter,
} from './printer.agent';
import { testReceipt, type AgentReceipt } from './printer.receipts';
import { isPrinterConfigured, usePrinterSettings } from './printer.settings';

export type PrinterState =
  'unconfigured' | 'unverified' | 'checking' | 'online' | 'offline';

// Partagé par toutes les fenêtres : l'état de l'imprimante est celui du poste, pas celui d'un écran.
const checking = ref(false);
const reachable = ref<boolean | null>(null);
const message = ref('');
const found = ref<FoundPrinter[]>([]);
const scanning = ref(false);
const scanned = ref(false);
const printing = ref(false);
let watching = false;

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : 'Impression impossible.';

export const usePrinter = () => {
  const settings = usePrinterSettings();

  const configured = computed(() => isPrinterConfigured(settings));

  // Une réponse ne vaut que pour la destination qui l'a donnée : en changer remet l'état à « à vérifier ».
  // Hors de tout composant : un observateur créé dans la fenêtre qui appelle `usePrinter` en premier mourrait avec elle.
  if (!watching) {
    watching = true;
    effectScope(true).run(() =>
      watch(
        () => [
          settings.agentUrl,
          settings.transport,
          settings.host,
          settings.port,
          settings.printerName,
        ],
        () => {
          reachable.value = null;
          message.value = '';
        },
      ),
    );
  }
  const state = computed<PrinterState>(() => {
    if (!configured.value) return 'unconfigured';
    if (checking.value) return 'checking';
    if (reachable.value === null) return 'unverified';
    return reachable.value ? 'online' : 'offline';
  });
  const target = computed(() =>
    settings.transport === 'network'
      ? `${settings.host}:${settings.port}`
      : settings.printerName,
  );

  // Vérifie que l'agent répond et qu'il joint l'imprimante : la même connexion sert ensuite aux impressions.
  const check = async () => {
    if (!configured.value) return false;
    checking.value = true;
    message.value = '';
    try {
      await connectPrinter(settings);
      reachable.value = true;
    } catch (error) {
      reachable.value = false;
      message.value = errorText(error);
    } finally {
      checking.value = false;
    }
    return reachable.value === true;
  };

  const scan = async () => {
    scanning.value = true;
    message.value = '';
    found.value = [];
    try {
      found.value = await scanPrinters(settings);
    } catch (error) {
      message.value = errorText(error);
    } finally {
      scanned.value = true;
      scanning.value = false;
    }
  };

  const choose = async (device: FoundPrinter) => {
    if (settings.transport === 'network') {
      settings.host = device.address ?? '';
      if (device.meta?.port) settings.port = device.meta.port;
    } else {
      settings.printerName = device.meta?.printerName ?? device.name;
    }
    settings.label = device.name;
    reachable.value = null;
    return check();
  };

  // Imprime et dit si ça a marché ; la raison de l'échec reste lisible dans `message`.
  const print = async (receipt: AgentReceipt) => {
    if (!configured.value) {
      message.value = 'Aucune imprimante choisie dans les réglages.';
      return false;
    }
    printing.value = true;
    message.value = '';
    try {
      await printReceipt(settings, receipt);
      reachable.value = true;
      return true;
    } catch (error) {
      reachable.value = false;
      message.value = errorText(error);
      return false;
    } finally {
      printing.value = false;
    }
  };

  const printTest = () => print(testReceipt(settings.label, Date.now()));

  return {
    settings,
    configured,
    state,
    target,
    message,
    found,
    scanning,
    scanned,
    printing,
    check,
    scan,
    choose,
    print,
    printTest,
  };
};
